/**
 * common CarouselMobile component used by other mudules as a reusable component which returns ui for gallery carousel component. required fields can be passed as props
 *
 * @module views/components/CarouselMobile
 * @memberof -Common
 */
import './CarouselMobile.scss';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import SwiperCore, {
  Keyboard,
  Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Icon from '@ulta/core/components/Icon/Icon';
import Image from '@ulta/core/components/Image/Image';
import { Link_Huge } from '@ulta/core/components/Link_Huge/Link_Huge';
import Video from '@ulta/core/components/Video/Video';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';
// install Swiper modules
SwiperCore.use( [Navigation, Keyboard] );

/**
 * Represents a MediaGallery component for mobile
 *
 * @method
 * @param {CarouselMobileProps} props - React properties passed from composition
 * @returns CarouselMobile
 */
const CarouselMobile = ( props ) => {
  const {
    items,
    imageWidth,
    ratio,
    tryItAction,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextClickAction,
    previousClickAction
  } = props;

  const carouselRef = useRef();
  const isSingleItem = items.length === 1;
  const ItemGroup = items?.length && items.map( ( item, index ) => {
    if( item.metaData?.mediaType === 'img' ){
      return (
        <SwiperSlide key={ index }>
          <div className={
            classNames( 'CarouselMobile--Image', {
              'CarouselMobile--SingleImage': isSingleItem
            }
            ) }
          >
            <Image
              src={ item?.imageUrl }
              alt={ item?.altText }
              key={ index }
              width={ imageWidth }
              aspectRatio={ ratio }
              metaData={ item?.metaData }
              ariaHidden={ item?.ariaHidden }
              ariaLabel={ carouselUtils.handleTokenizedString( carouselCardAccessibility, [index + 1, items.length] ) }
            />
          </div>
        </SwiperSlide>
      );
    }
    else {
      return (
        <SwiperSlide key={ index }>
          <div className={
            classNames( 'CarouselMobile--Video', {
              'CarouselMobile--SingleVideo': isSingleItem
            } ) }
          >
            <Video
              imageUrl={ item?.imageUrl }
              videoUrl={ item?.videoUrl }
              alt={ item?.altText }
              closedCaptionUrl={ item.closedCaptionUrl }
              metaData={ item?.metaData }
              ariaLabel={ carouselUtils.handleTokenizedString( carouselCardAccessibility, [index + 1, items.length] ) }
              isCaptionsEnabled={ item.isCaptionsEnabled }
              videoProfiles={ item.videoProfiles }
              preloadNone={ 'none' }
            />
          </div>
        </SwiperSlide>
      );
    }
  } );
  return (
    <div className='CarouselMobile'
      role='region'
      aria-label={ carouselAccessibility }
    >
      { ItemGroup.length > 1 ? (
        <>
          <p className='sr-only'>{ carouselDetailsAccessibility }</p>
          <Swiper
            ref={ carouselRef }
            freeMode={ true }
            longSwipes={ true }
            speed={ 600 }
            preloadImages={ true }
            spaceBetween={ 16 }
            pagination={ {
              type: 'bullets',
              clickable: 'true'
            } }
            breakpoints={ {
              768: {
                slidesPerView: 1.5
              },
              0: {
                slidesPerView: 1.2
              }
            } }
          >
            { ItemGroup }
          </Swiper>
        </>
      ) :
        ItemGroup[0]
      }
      { tryItAction?.url && tryItAction?.label &&
      <Link_Huge
        compact={ true }
        action={ tryItAction }
      >
        <Icon
          size='s'
          name={ 'Tryon' }
        />
        { tryItAction.label }
      </Link_Huge>
      }
    </div>
  );
};


/**
  * Property type definitions
  * @typedef CarouselMobileProps
  * @type {object}
  * @property {array} items - Sets the items as images and videos.
  * @property {number} imageWidth - Sets the width of image
  * @property {string} ratio - Sets the ratio of image
  * @property {object} tryItAction - Set the try it link and label
  * @property {string} carouselAccessibility - Sets the carousel accessibility
  * @property {string} carouselDetailsAccessibility - Sets the carousel details accessibility
  * @property {string} carouselCardAccessibility - Sets the carousel card accessibility
  */
export const propTypes = {
  /**
   * The elements to display as slides in the carousel
   */
  items: PropTypes.array.isRequired,
  /**
   * The width of image
   */
  imageWidth: PropTypes.number,
  /**
   * The ratio of image
   */
  ratio: PropTypes.string,
  /**
   * The prop that hold the label and link url for try it.
   */
  tryItAction: PropTypes.object,
  /**
   * The prop that hold the carousel accessibility
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that hold the carousel details accessibility.
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel card accessibility
   */
  carouselCardAccessibility: PropTypes.string
};

CarouselMobile.propTypes = propTypes;

export default CarouselMobile;